import React, { useState } from "react";
import { Button, Tabs, Avatar, message, Empty } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { cancelMoneyRequest, moneyRequestList , sendRemainder } from "../../../api/WalletApi";
import AuthService from "../../../session/AuthService";
import dayjs from "dayjs";
import { getCurrencySymbol } from "../../../Custom/Custom";
import { useGlobalUI } from "../../../context/GlobalUIProvider";
import { payNow } from "../../../api/UserAPI";

const { TabPane } = Tabs;

const MoneyRequests = ({ onBack, getUserDetails }) => {
  const [view, setView] = useState("list"); // "list" or "detail"
  const [activeTab, setActiveTab] = useState("forMe");
  const [selected, setSelected] = useState(null);
  const [moneyRequest, setMoneyRequest] = useState([]);

  const { showLoader, hideLoader, message } = useGlobalUI();

  useEffect(() => {
    getAllRequests();
  }, []);

  const authId = AuthService.getAuthId();

  const getAllRequests = () => {
    moneyRequestList().then((response) => {
      if (response.data.status === 1) {
        const requests = response.data.data;

        const forMe = [];
        const fromMe = [];

        requests.forEach((req) => {
          const isForMe = req.toUser?.id === authId; // someone requested money from me
          const isFromMe = req.fromUser?.id === authId; // I requested money from someone

          const formattedDate = dayjs(req.createdAt).format("MMM DD, HH:mm");

          if (isForMe) {
            forMe.push({
              id: req.id,
              from: req.fromUser?.name,
              mobile: req.fromUser?.mobileNo,
              reason: req.reason || "—", // add field if API supports reason
              amount: `${req.amount}`,
              date: formattedDate,
              fromNationality: req.fromUser?.nationality,
            });
          }

          if (isFromMe) {
            fromMe.push({
              id: req.id,
              to: [req.toUser?.name],
              mobile: req.toUser?.mobileNo,
              reason: req.reason || "—",
              amount: `${req.amount}`,
              total: `${req.amount}`, // same as amount unless grouped
              toNationalities: [req.toUser?.nationality],
            });
          }
        });

        const finalData = { forMe, fromMe };
        setMoneyRequest(finalData); // 👈 now your state has structured data
      }
    });
  };

  const cancelmoneyRequest = (value) => {
    showLoader();
    let obj = {
      requestId: value,
    };

    cancelMoneyRequest(obj).then((response) => {
      hideLoader();
      if (response.data.status === 1) {
        message.success(response.data.message);
        getAllRequests();
        setView("list");
      } else {
        message.error(response.data.message);
      }
    });
  };

  const payRequestMoney = (selected) => {
    let obj = {
      id: AuthService.getAuthId(),
      mobileNo: selected.mobile,
      amountSend: selected.amount,
      requestId: selected.id,
    };
    payNow(obj).then((response) => {
      if (response.data.status === 1) {
        message.success(response.data.message);
        getAllRequests();
        getUserDetails();
        setView("list");
      } else {
        message.error(response.data.message);
      }
    });
  };


  const handleSendReminder = async (requestId) => {
  try {
    showLoader(); // optional loader
    const payload = { requestId }; // send requestId in payload
    const res = await sendRemainder(payload);

    if (res.status === 1) {
      message.success("Reminder sent successfully!");
    } else {
      message.error(res.message || "Failed to send reminder");
    }
  } catch (err) {
    console.error(err);
    message.error("Something went wrong while sending reminder");
  } finally {
    hideLoader();
  }
};




  const RequestItem = ({ request, type, onClick }) => {
    // pick the right nationality
    const nationality =
      type === "forMe" ? request.fromNationality : request.toNationalities?.[0]; // if multiple, just first one

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 0",
          borderBottom: "1px solid #f0f0f0",
          cursor: "pointer",
        }}
        onClick={onClick}
      >
        <Avatar size={40} style={{ marginRight: 12 }}>
          {type === "forMe" ? request.from?.[0] : request.to?.[0][0]}
        </Avatar>

        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {getCurrencySymbol(nationality)}
            {request.amount}
          </div>

          <div style={{ fontSize: 13, color: "#888" }}>
            {type === "forMe"
              ? `From ${request.from}`
              : `To ${request.to.join(", ")}`}
          </div>
        </div>

        <RightArrow />
      </div>
    );
  };

  // Arrow (inline, for reuse)
  const RightArrow = () => (
    <span style={{ fontSize: 16, color: "#999" }}>›</span>
  );

  // Render Details View
  const renderDetail = () => {
    const isForMe = activeTab === "forMe";

    return (
      <motion.div
        key="detail"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        style={{ padding: 24, minHeight: "100vh", background: "#fff" }}
      >
        {/* Header */}
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 24 }}
        >
          <LeftOutlined
            style={{ fontSize: 20, cursor: "pointer" }}
            onClick={() => setView("list")}
          />
          <div style={{ flex: 1, textAlign: "center", fontWeight: 600 }}>
            {isForMe ? "Money Request for me" : "Money Request by me"}
          </div>
        </div>

        {/* Detail Content */}
        {isForMe ? (
          <>
            <Avatar size={60} style={{ marginBottom: 16 }}>
              {selected.from?.[0]}
            </Avatar>

            <h2 style={{ fontWeight: 700, fontSize: 22 }}>
              {selected.amount}&nbsp;{getCurrencySymbol(selected.nationality)}
            </h2>
            <div style={{ fontWeight: 600 }}>{selected.from}</div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 24 }}>
              {selected.date}
            </div>

            <DetailRow label="Reason" value={selected.reason} type={""} />
            <DetailRow
              label="Amount"
              value={selected.amount}
              type={getCurrencySymbol(selected.nationality)}
            />

            {/* Actions */}
            <ActionButton
              type="primary"
              onClick={() => payRequestMoney(selected)}
            >
              Pay now
            </ActionButton>
            <ActionButton onClick={() => cancelmoneyRequest(selected.id)}>
              Reject the request
            </ActionButton>
          </>
        ) : (
          <>
            {/* Multiple recipients */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              {selected.to.map((person, idx) => (
                <div key={idx} style={{ textAlign: "center" }}>
                  <Avatar size={50}>{person[0]}</Avatar>
                  <div style={{ fontSize: 12 }}>{person}</div>
                </div>
              ))}
            </div>

            <h2 style={{ fontWeight: 700, fontSize: 22 }}>
              {selected.total} &nbsp; {getCurrencySymbol(selected.nationality)}
            </h2>

            <DetailRow label="Reason" value={selected.reason} />
            <DetailRow
              label="Amount"
              value={selected.amount}
              type={getCurrencySymbol(selected.nationality)}
            />

            {/* Actions */}
            <ActionButton type="primary"   onClick={() => handleSendReminder(selected.id)}>Send reminder</ActionButton>
            <ActionButton onClick={() => cancelmoneyRequest(selected.id)}>
              Cancel
            </ActionButton>
          </>
        )}
      </motion.div>
    );
  };

  /* 🔹 Reusable small components to cut repetition */

  const DetailRow = ({ label, value, type }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: "#888", fontSize: 13 }}>{label}</div>
      <div style={{ fontWeight: 600 }}>
        {value}&nbsp;{type}
      </div>
    </div>
  );

  const ActionButton = ({ type, children, onClick }) => (
    <Button
      type={type}
      block
      style={{
        marginBottom: 12,
        background: type === "primary" ? "#0575E6" : "#f5f5f5",
        border: "none",
        height: 48,
        color: type === "primary" ? "#fff" : "#000",
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  );

  // Render List View
  const renderList = () => (
    <motion.div
      key="list"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      style={{ padding: 24, minHeight: "100vh", background: "#fff" }}
    >
      {/* Header with Back Icon */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <LeftOutlined
          style={{ fontSize: 20, cursor: "pointer", marginRight: 8 }}
          onClick={() => {
            onBack();
            // action on back (maybe go to Home or previous screen)
            console.log("Back clicked");
          }}
        />
        <h2
          style={{ flex: 1, textAlign: "center", fontWeight: 600, margin: 0 }}
        >
          Money Requests
        </h2>
        {/* Spacer to balance the center alignment */}
        <div style={{ width: 20 }} />
      </div>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
        <TabPane tab="For me" key="forMe">
          {moneyRequest.forMe?.length > 0 ? (
            moneyRequest.forMe.map((req) => (
              <RequestItem
                key={req.id}
                request={req}
                type="forMe"
                onClick={() => {
                  setSelected(req);
                  setView("detail");
                }}
              />
            ))
          ) : (
            <Empty
              description="No requests for you"
              style={{ marginTop: 40 }}
            />
          )}
        </TabPane>

        <TabPane tab="From me" key="fromMe">
          {moneyRequest.fromMe?.length > 0 ? (
            moneyRequest.fromMe.map((req) => (
              <RequestItem
                key={req.id}
                request={req}
                type="fromMe"
                onClick={() => {
                  setSelected(req);
                  setView("detail");
                }}
              />
            ))
          ) : (
            <Empty
              description="No requests from you"
              style={{ marginTop: 40 }}
            />
          )}
        </TabPane>
      </Tabs>
    </motion.div>
  );

  return view === "list" ? renderList() : renderDetail();
};

export default MoneyRequests;
